import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import {
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Package,
  RefreshCw,
  Save,
} from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Dialog, DialogContent } from 'components/Dialog';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Input } from 'components/input/Input';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { FormatPhoneNumber } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { useToast } from 'hooks/useToaster';
import { createCommodity } from 'lib/api/createCommodity';
import { getCustomers } from 'lib/api/getCustomers';
import { searchCommodities } from 'lib/api/searchCommodities';
import { TMSCommodity, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { calculateDensity } from 'utils/calculateDensity';
import {
  GenericCompanySearchableFields,
  customerSearchHandler,
  mapCustomerToSelectOptions,
} from 'utils/loadInfoAndBuilding';

interface CommodityCreatorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCommodityCreated: (commodity: TMSCommodity) => void;
  /** Optional callback to let the parent refetch the commodity list after a successful TMS commodity search. */
  onCommoditiesRefreshed?: (customerID: number) => void;
  tmsID: number;
  initialData?: Partial<CommodityFormData>;
  customerBK?: string; // Optional: customerBK from selected customer in form
  /** Use the main form's customer list (same as Customer dropdown) so selected customer is always in list */
  initialCustomers?: Maybe<TMSCustomer[]>;
  initialIsLoadingCustomers?: boolean;
}

interface CommodityFormData {
  customerBK: string; // Selected customer's externalTMSID
  commodityDescription: string;
  weightAmount: number;
  weightUnitId: number;
  pieceCount: number;
  handlingUnitCount: number;
  stackable: boolean;
  length?: number;
  width?: number;
  height?: number;
  dimUnitId?: number;
  carrierType: number;
  // HazMat fields
  hazmatGroupId?: number;
  hazmatClassId?: number;
  hazmatPrefixId?: number;
  hazmatCode?: string;
  hazmatChemicalName?: string;
  hazmatEmergencyContactNumber?: string;
}

const weightUnitOptions = [
  { value: 0, label: 'Kg' },
  { value: 1, label: 'Lbs' },
];

const dimUnitOptions = [
  { value: 0, label: 'Inches' },
  { value: 1, label: 'Feet' },
];

const carrierTypeOptions = [{ value: 3, label: 'TL' }];

const hazmatGroupOptions = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Class I' },
  { value: 2, label: 'Class II' },
  { value: 3, label: 'Class III' },
];

const hazmatClassOptions = [
  { value: 0, label: 'Class not specified' },
  { value: 1, label: 'Class 1 Explosive: division not specified' },
  { value: 11, label: 'Class 1.1 Explosive: mass' },
  { value: 12, label: 'Class 1.2 Explosive: projection' },
  { value: 13, label: 'Class 1.3 Explosive: fire and minor blast/projection' },
  { value: 14, label: 'Class 1.4 Explosive: minor' },
  { value: 15, label: 'Class 1.5 Explosive: insensitive mass' },
  { value: 16, label: 'Class 1.6 Explosive: insensitive minor' },
  { value: 2, label: 'Class 2 Gas: division not specified' },
  { value: 21, label: 'Class 2.1 Gas: flammable' },
  { value: 22, label: 'Class 2.2 Gas: nonflammable/nonpoisonous/oxygen' },
  { value: 23, label: 'Class 2.3 Gas: poison' },
  { value: 3, label: 'Class 3 Flammable Liquid and Combustible Liquid' },
  { value: 4, label: 'Class 4 Solid: division not specified' },
  { value: 41, label: 'Class 4.1 Flammable Solid' },
  { value: 42, label: 'Class 4.2 Spontaneously Combustible Solid' },
  { value: 43, label: 'Class 4.3 Dangerous When Wet' },
  { value: 5, label: 'Class 5 Oxidizer: division not specified' },
  { value: 51, label: 'Class 5.1 Oxidizer' },
  { value: 52, label: 'Class 5.2 Organic Peroxide' },
  { value: 6, label: 'Class 6 Poison, Toxic or Inhalation Hazard' },
  { value: 61, label: 'Class 6.1 Toxic and Infectious Substances' },
  { value: 7, label: 'Class 7 Radioactive Materials' },
  { value: 8, label: 'Class 8 Corrosive' },
  { value: 9, label: 'Class 9 Miscellaneous' },
];

const hazmatPrefixOptions = [
  { value: 0, label: 'UN' },
  { value: 1, label: 'NA' },
];

export default function CommodityCreatorDialog({
  isOpen,
  onClose,
  onCommodityCreated,
  onCommoditiesRefreshed,
  tmsID,
  initialData,
  customerBK: initialCustomerBK,
  initialCustomers,
  initialIsLoadingCustomers,
}: CommodityCreatorDialogProps) {
  const [showHazmat, setShowHazmat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [refreshKeyword, setRefreshKeyword] = useState('');
  const [isRefreshingCommodities, setIsRefreshingCommodities] = useState(false);
  const { toast } = useToast();
  const isMountedRef = useRef(true);

  const formMethods = useForm<CommodityFormData>({
    defaultValues: {
      customerBK: initialCustomerBK || '',
      commodityDescription: initialData?.commodityDescription || '',
      weightAmount: initialData?.weightAmount || 0,
      weightUnitId: initialData?.weightUnitId ?? 1, // Default to Lbs
      pieceCount: initialData?.pieceCount || 1,
      handlingUnitCount: initialData?.handlingUnitCount || 1,
      stackable: initialData?.stackable || false,
      length: initialData?.length,
      width: initialData?.width,
      height: initialData?.height,
      dimUnitId: initialData?.dimUnitId || 0, // Default to Inches
      carrierType: initialData?.carrierType ?? 3, // Default to TL
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods;

  const handleClose = () => {
    reset();
    setShowHazmat(false);
    setRefreshKeyword('');
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setRefreshKeyword('');
      reset(
        {
          customerBK: initialCustomerBK || '',
          commodityDescription: initialData?.commodityDescription || '',
          weightAmount: initialData?.weightAmount || 0,
          weightUnitId: initialData?.weightUnitId ?? 1,
          pieceCount: initialData?.pieceCount || 1,
          handlingUnitCount: initialData?.handlingUnitCount || 1,
          stackable: initialData?.stackable || false,
          length: initialData?.length,
          width: initialData?.width,
          height: initialData?.height,
          dimUnitId: initialData?.dimUnitId || 0,
          carrierType: initialData?.carrierType ?? 3,
          hazmatGroupId: initialData?.hazmatGroupId,
          hazmatClassId: initialData?.hazmatClassId,
          hazmatPrefixId: initialData?.hazmatPrefixId,
          hazmatCode: initialData?.hazmatCode,
          hazmatChemicalName: initialData?.hazmatChemicalName,
          hazmatEmergencyContactNumber:
            initialData?.hazmatEmergencyContactNumber,
        },
        {
          keepDefaultValues: false,
        }
      );

      // Hazmat accordion is hidden for now - TODO: Don't have proper hazmat details to test this feature
      // Always keep showHazmat as false since the accordion is hidden
      setShowHazmat(false);
    }
  }, [isOpen, initialData, initialCustomerBK, reset]);

  // Set mounted ref to true on mount, false on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Use parent's customer list when modal opens (same list as main Customer dropdown)
  // Don't overwrite with null while parent is still loading — wait for definitive data so we
  // don't show an empty dropdown if the user opened the dialog before the parent fetch completed.
  useEffect(() => {
    if (isOpen) {
      if (initialIsLoadingCustomers !== undefined) {
        setIsLoadingCustomers(initialIsLoadingCustomers);
      }
      if (initialCustomers !== undefined) {
        if (!initialIsLoadingCustomers || initialCustomers !== null) {
          setCustomers(initialCustomers ?? null);
        }
      }
    }
  }, [isOpen, initialCustomers, initialIsLoadingCustomers]);

  const handleRefreshCustomers = async () => {
    if (!tmsID) return;

    setIsLoadingCustomers(true);

    const res = await getCustomers(tmsID, true);

    if (!isMountedRef.current) {
      return; // Return immediately without any state updates
    }

    if (res.isOk()) {
      setCustomers(res.value.customerList);
      toast({
        description: 'Successfully refreshed customer list.',
        variant: 'success',
      });
      setIsLoadingCustomers(false);
      return;
    }
    toast({
      description: res.error.message || 'Error while refreshing customer list.',
      variant: 'destructive',
    });
    setIsLoadingCustomers(false);
  };

  const handleCustomerSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ) => {
    return customerSearchHandler({
      tmsID,
      customers,
      setCustomers,
      field,
      value,
    });
  };

  const watchedCustomerBK = useWatch({ control, name: 'customerBK' });
  const customerIDForSearch = useMemo(
    () =>
      customers?.find((c) => c.externalTMSID === watchedCustomerBK)?.ID ?? null,
    [customers, watchedCustomerBK]
  );

  const handleSearchCommoditiesFromTMS = async () => {
    const keyword = refreshKeyword.trim();
    if (!tmsID || !customerIDForSearch || !keyword) {
      toast({
        description: 'Please select a customer and enter a search term.',
        variant: 'destructive',
      });
      return;
    }
    setIsRefreshingCommodities(true);
    const res = await searchCommodities(tmsID, customerIDForSearch, keyword);
    setIsRefreshingCommodities(false);
    if (res.isErr()) {
      toast({
        description: res.error.message || 'Commodity search failed.',
        variant: 'destructive',
      });
      return;
    }
    const list = res.value.commodityList;
    if (!list.length) {
      toast({
        description: 'No matching commodities found.',
        variant: 'default',
      });
      return;
    }
    toast({
      description: `Found ${list.length} matching commodity(ies). List refreshed.`,
      variant: 'success',
    });
    onCommoditiesRefreshed?.(customerIDForSearch);
    handleClose();
  };

  // Watch dimension and weight fields for density calculation
  const watchedLength = useWatch({ control, name: 'length' });
  const watchedWidth = useWatch({ control, name: 'width' });
  const watchedHeight = useWatch({ control, name: 'height' });
  const watchedDimUnitId = useWatch({ control, name: 'dimUnitId' });
  const watchedWeightAmount = useWatch({ control, name: 'weightAmount' });
  const watchedWeightUnitId = useWatch({ control, name: 'weightUnitId' });
  const watchedHandlingUnitCount = useWatch({
    control,
    name: 'handlingUnitCount',
  });

  // Calculate density in lb/cu ft using utility function
  const calculatedDensity = useMemo(() => {
    return calculateDensity(
      watchedWeightAmount,
      watchedWeightUnitId,
      watchedLength,
      watchedWidth,
      watchedHeight,
      watchedDimUnitId,
      watchedHandlingUnitCount
    );
  }, [
    watchedLength,
    watchedWidth,
    watchedHeight,
    watchedDimUnitId,
    watchedWeightAmount,
    watchedWeightUnitId,
    watchedHandlingUnitCount,
  ]);

  const onSubmit = async (data: CommodityFormData) => {
    setIsLoading(true);

    try {
      // Build hazmat object if hazmat fields are filled
      const hazmatData =
        showHazmat &&
        (data.hazmatGroupId !== undefined ||
          data.hazmatClassId !== undefined ||
          data.hazmatCode ||
          data.hazmatChemicalName ||
          data.hazmatEmergencyContactNumber)
          ? {
              chemicalName: data.hazmatChemicalName || '',
              class: null,
              classId: data.hazmatClassId ?? 0,
              code: data.hazmatCode || '',
              flag: true,
              group: null,
              groupId: data.hazmatGroupId ?? 0,
              prefixId: data.hazmatPrefixId ?? 0,
              emergencyContactNumber: data.hazmatEmergencyContactNumber || '',
            }
          : undefined;

      // Ensure carrierType defaults to 3 (TL) if not set (undefined/null), but allow 0 as valid value
      const carrierType =
        data.carrierType !== undefined && data.carrierType !== null
          ? data.carrierType
          : 3;

      const commodityPayload: TMSCommodity = {
        ID: undefined,
        externalTMSID: '', // Will be assigned by backend
        commodityDescription: data.commodityDescription,
        weightAmount: data.weightAmount,
        weightUnitId: data.weightUnitId,
        pieceCount: data.pieceCount,
        handlingUnitCount: data.handlingUnitCount,
        nmfcNumber: '', // Always send empty string as per API
        freightClassId: null, // Always send null as per API
        stackable: data.stackable,
        length: data.length,
        width: data.width,
        height: data.height,
        dimUnitId: data.dimUnitId,
        carrierType: carrierType,
        density: calculatedDensity ?? undefined,
        hazmatClass: hazmatData?.class ?? null,
        hazmatClassId: hazmatData?.classId ?? undefined,
        hazmatCode: hazmatData?.code ?? null,
        hazmatGroup: hazmatData?.group ?? null,
        hazmatGroupId: hazmatData?.groupId ?? undefined,
        hazmatPrefixId: hazmatData?.prefixId ?? undefined,
        hazmatChemicalName: hazmatData?.chemicalName ?? null,
        hazmatEmergencyContactNumber:
          hazmatData?.emergencyContactNumber ?? null,
      };

      const options = data.customerBK
        ? { customerBK: data.customerBK }
        : undefined;

      const res = await createCommodity({
        commodity: commodityPayload,
        tmsID,
        ...(options && { options }),
      });

      if (!isMountedRef.current) {
        return; // Return immediately without any state updates
      }

      if (res.isOk()) {
        onCommodityCreated(res.value.commodity);
        toast({
          description: 'Commodity created successfully.',
          variant: 'success',
        });
        handleClose();
      } else {
        // Show user-friendly error message
        const errorMessage = res.error.message || 'Failed to create commodity';
        // Check if error message contains technical details (URLs, HTTP status codes, etc.)
        const isTechnicalError =
          errorMessage.includes('http://') ||
          errorMessage.includes('https://') ||
          errorMessage.includes('POST ') ||
          errorMessage.includes('GET ') ||
          errorMessage.includes('400') ||
          errorMessage.includes('500') ||
          errorMessage.includes('Bad Request') ||
          errorMessage.includes('Microsoft-Azure');

        toast({
          description: isTechnicalError
            ? 'Failed to create commodity. Please check your inputs and try again.'
            : errorMessage,
          variant: 'destructive',
        });
      }
    } catch {
      if (!isMountedRef.current) {
        return; // Return immediately without any state updates
      }
      toast({
        description: 'Failed to create commodity. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className='max-w-md p-0 max-h-[90vh] flex flex-col border border-neutral-300 overflow-hidden'>
          <ExtendedFormProvider
            aiDefaultValues={!!initialData}
            aiIconOnly={true}
          >
            <FormProvider {...formMethods}>
              <Flex
                justify='between'
                align='center'
                className='bg-brand-main px-4 py-3 text-neutral-50 border-b rounded-t-lg [&>div>svg]:stroke-white!'
              >
                <Flex align='center' gap='sm'>
                  <Package className='h-5 w-5' />
                  <Typography variant='h3' className='text-neutral-50'>
                    Create Shipment Item
                  </Typography>
                </Flex>
              </Flex>

              <form
                onSubmit={(e) => {
                  e.stopPropagation();
                  handleSubmit(onSubmit)(e);
                }}
                className='flex flex-1 flex-col overflow-hidden'
              >
                <div className='flex-1 space-y-4 overflow-y-auto px-4 pb-4'>
                  {/* Customer Selection */}
                  <div className='p-3 pt-0'>
                    <RHFDebounceSelect
                      required
                      name='customerBK'
                      label='Customer'
                      control={control}
                      errors={errors}
                      data={customers}
                      isLoading={isLoadingCustomers}
                      refreshHandler={handleRefreshCustomers}
                      fetchOptions={handleCustomerSearch}
                      mapOptions={mapCustomerToSelectOptions}
                      showSearchParamDropdown={false}
                      searchFieldDefault='name'
                      placeholder='Search by customer name'
                    />
                  </div>

                  {/* Search TMS for this commodity (same pattern as LocationCreatorDialog "Search GTZ for this location") */}
                  <div className='p-3 pt-0'>
                    <div>
                      <Label
                        htmlFor='refreshKeyword'
                        name='refreshKeyword'
                        className='mb-1 inline-flex items-center gap-1'
                      >
                        Search TMS for this commodity
                        <Tooltip>
                          <TooltipTrigger
                            type='button'
                            className='flex items-center justify-center text-neutral-400 hover:text-neutral-600 cursor-help'
                          >
                            <CircleAlert className='h-3.5 w-3.5' />
                          </TooltipTrigger>
                          <TooltipContent
                            side='right'
                            align='center'
                            sideOffset={8}
                            className='max-w-[180px] whitespace-normal break-words text-[11px] leading-snug'
                          >
                            Can't find the commodity? Type a keyword and click
                            Search. We'll search your TMS (e.g. GlobalTranz) and
                            add matching commodities to the dropdown.
                          </TooltipContent>
                        </Tooltip>
                      </Label>
                      <Flex align='center' gap='sm'>
                        <Input
                          id='refreshKeyword'
                          value={refreshKeyword}
                          onChange={(e) => setRefreshKeyword(e.target.value)}
                          placeholder='Search by description or name'
                          onKeyDown={(e) =>
                            e.key === 'Enter' &&
                            (e.preventDefault(),
                            handleSearchCommoditiesFromTMS())
                          }
                        />
                        <button
                          type='button'
                          title='Search commodities in TMS'
                          onClick={handleSearchCommoditiesFromTMS}
                          className={`shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors ${
                            !customerIDForSearch ||
                            !refreshKeyword.trim() ||
                            isRefreshingCommodities
                              ? 'cursor-not-allowed text-neutral-300'
                              : 'cursor-pointer'
                          }`}
                          disabled={
                            !customerIDForSearch ||
                            !refreshKeyword.trim() ||
                            isRefreshingCommodities
                          }
                        >
                          {isRefreshingCommodities ? (
                            <ButtonLoader className='mr-0' />
                          ) : (
                            <RefreshCw className='h-4 w-4' />
                          )}
                        </button>
                      </Flex>
                    </div>
                  </div>

                  {/* Commodity Description */}
                  <div className='p-3 pt-0'>
                    <div>
                      <Label
                        htmlFor='commodityDescription'
                        required
                        name='commodityDescription'
                        className='mb-1 inline-block'
                      >
                        Commodity Description
                      </Label>
                      <Input
                        id='commodityDescription'
                        {...register('commodityDescription', {
                          required: 'Commodity description is required',
                        })}
                        placeholder='Enter commodity description'
                        className={
                          errors.commodityDescription ? 'border-error-500' : ''
                        }
                      />
                      {errors.commodityDescription && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.commodityDescription.message}
                        </Typography>
                      )}
                    </div>
                  </div>

                  {/* Weight and Piece Count */}
                  <Grid cols='2' gap='sm' className='p-3 pt-0'>
                    <div>
                      <Label
                        htmlFor='weightAmount'
                        required
                        name='weightAmount'
                        className='mb-1 inline-block'
                      >
                        Weight Per Piece
                      </Label>
                      <Input
                        id='weightAmount'
                        type='number'
                        step='0.01'
                        {...register('weightAmount', {
                          required: 'Weight per piece is required',
                          min: {
                            value: 0.01,
                            message: 'Weight must be greater than 0',
                          },
                          valueAsNumber: true,
                        })}
                        placeholder='0.00'
                        className={
                          errors.weightAmount ? 'border-error-500' : ''
                        }
                      />
                      {errors.weightAmount && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.weightAmount.message}
                        </Typography>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor='weightUnitId'
                        name='weightUnitId'
                        className='mb-1 inline-block'
                      >
                        Weight Unit
                      </Label>
                      <Controller
                        name='weightUnitId'
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            value={
                              field.value !== undefined && field.value !== null
                                ? String(field.value)
                                : ''
                            }
                          >
                            <SelectTrigger className='w-full mt-1'>
                              <SelectValue placeholder='Select weight unit' />
                            </SelectTrigger>
                            <SelectContent>
                              {weightUnitOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={String(option.value)}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </Grid>

                  {/* Piece Count and Pallet Count */}
                  <Grid cols='2' gap='sm' className='p-3 pt-0'>
                    <div>
                      <Label
                        htmlFor='pieceCount'
                        name='pieceCount'
                        className='mb-1 inline-block'
                      >
                        Piece Count
                      </Label>
                      <Input
                        id='pieceCount'
                        type='number'
                        {...register('pieceCount', {
                          min: { value: 1, message: 'Must be at least 1' },
                          valueAsNumber: true,
                        })}
                        placeholder='1'
                        className={errors.pieceCount ? 'border-error-500' : ''}
                      />
                      {errors.pieceCount && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.pieceCount.message}
                        </Typography>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor='handlingUnitCount'
                        required
                        name='handlingUnitCount'
                        className='mb-1 inline-block'
                      >
                        Pallet Count
                      </Label>
                      <Input
                        id='handlingUnitCount'
                        type='number'
                        {...register('handlingUnitCount', {
                          required: 'Pallet count is required',
                          min: { value: 1, message: 'Must be at least 1' },
                          valueAsNumber: true,
                        })}
                        placeholder='1'
                        className={
                          errors.handlingUnitCount ? 'border-error-500' : ''
                        }
                      />
                      {errors.handlingUnitCount && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.handlingUnitCount.message}
                        </Typography>
                      )}
                    </div>
                  </Grid>

                  {/* Carrier Type */}
                  <div className='p-3 pt-0'>
                    <Label
                      htmlFor='carrierType'
                      name='carrierType'
                      className='mb-1 inline-block'
                    >
                      Carrier Type
                    </Label>
                    <Controller
                      name='carrierType'
                      control={control}
                      defaultValue={3}
                      render={({ field }) => (
                        <Select
                          onValueChange={(value) =>
                            field.onChange(Number(value))
                          }
                          value={
                            field.value !== undefined && field.value !== null
                              ? String(field.value)
                              : '3'
                          }
                        >
                          <SelectTrigger className='w-full mt-1'>
                            <SelectValue placeholder='Select carrier type' />
                          </SelectTrigger>
                          <SelectContent>
                            {carrierTypeOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={String(option.value)}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {/* Stackable */}
                  <div className='p-3 pt-0'>
                    <Controller
                      name='stackable'
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id='stackable'
                          label='Stackable'
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                  </div>

                  {/* Dimensions */}
                  <div className='p-3 pt-0'>
                    <Typography
                      variant='body'
                      weight='semibold'
                      className='mb-2'
                    >
                      Dimensions
                    </Typography>
                    <Grid cols='3' gap='sm' className='mb-2'>
                      <div>
                        <Label
                          htmlFor='length'
                          name='length'
                          className='mb-1 inline-block'
                        >
                          Length
                        </Label>
                        <Input
                          id='length'
                          type='number'
                          step='0.01'
                          {...register('length', {
                            valueAsNumber: true,
                          })}
                          placeholder='0.00'
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor='width'
                          name='width'
                          className='mb-1 inline-block'
                        >
                          Width
                        </Label>
                        <Input
                          id='width'
                          type='number'
                          step='0.01'
                          {...register('width', {
                            valueAsNumber: true,
                          })}
                          placeholder='0.00'
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor='height'
                          name='height'
                          className='mb-1 inline-block'
                        >
                          Height
                        </Label>
                        <Input
                          id='height'
                          type='number'
                          step='0.01'
                          {...register('height', {
                            valueAsNumber: true,
                          })}
                          placeholder='0.00'
                        />
                      </div>
                    </Grid>
                    <div className='mb-2'>
                      <Label
                        htmlFor='dimUnitId'
                        name='dimUnitId'
                        className='mb-1 inline-block'
                      >
                        Dimension Unit
                      </Label>
                      <Controller
                        name='dimUnitId'
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            value={
                              field.value !== undefined && field.value !== null
                                ? String(field.value)
                                : ''
                            }
                          >
                            <SelectTrigger className='w-full mt-1'>
                              <SelectValue placeholder='Select dimension unit' />
                            </SelectTrigger>
                            <SelectContent>
                              {dimUnitOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={String(option.value)}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    {/* Density Display */}
                    {calculatedDensity !== null && (
                      <div>
                        <Label
                          htmlFor='density'
                          name='density'
                          className='mb-1 inline-block'
                        >
                          Density (lb/cu ft)
                        </Label>
                        <Input
                          id='density'
                          type='text'
                          value={calculatedDensity.toFixed(2)}
                          readOnly
                          className='bg-neutral-100 cursor-not-allowed'
                        />
                      </div>
                    )}
                  </div>

                  {/* HazMat Toggle - Hidden for now */}
                  {/* TODO: Don't have proper hazmat details to test this feature - will add hazmat accordion in future */}
                  {/* eslint-disable-next-line no-constant-binary-expression */}
                  {false && (
                    <div className='p-3 pt-0'>
                      <button
                        type='button'
                        onClick={() => setShowHazmat(!showHazmat)}
                        className='flex items-center gap-2 text-info-600 hover:text-info-700'
                      >
                        {showHazmat ? (
                          <ChevronUp className='h-4 w-4' />
                        ) : (
                          <ChevronDown className='h-4 w-4' />
                        )}
                        <Typography variant='body-sm' weight='semibold'>
                          HazMat
                        </Typography>
                      </button>
                    </div>
                  )}

                  {/* HazMat Fields - Hidden for now */}
                  {/* TODO: Don't have proper hazmat details to test this feature - will add hazmat accordion in future */}
                  {/* eslint-disable-next-line no-constant-binary-expression */}
                  {false && showHazmat && (
                    <div className='p-3 pt-0 border-t border-neutral-200 pt-4'>
                      {/* Hazmat Group */}
                      <div className='mt-2'>
                        <Label
                          htmlFor='hazmatGroupId'
                          required
                          name='hazmatGroupId'
                          className='mb-1 inline-block'
                        >
                          Hazmat Group
                        </Label>
                        <Controller
                          name='hazmatGroupId'
                          control={control}
                          rules={{
                            required: showHazmat
                              ? 'Hazmat group is required'
                              : false,
                          }}
                          render={({ field }) => (
                            <Select
                              onValueChange={(value) =>
                                field.onChange(Number(value))
                              }
                              value={
                                field.value !== undefined &&
                                field.value !== null
                                  ? String(field.value)
                                  : ''
                              }
                            >
                              <SelectTrigger className='w-full mt-1'>
                                <SelectValue placeholder='Select hazmat group' />
                              </SelectTrigger>
                              <SelectContent>
                                {hazmatGroupOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={String(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.hazmatGroupId && (
                          <Typography
                            variant='body-sm'
                            className='text-error-500'
                          >
                            {errors.hazmatGroupId?.message}
                          </Typography>
                        )}
                      </div>

                      {/* Hazmat Class */}
                      <div className='mt-2'>
                        <Label
                          htmlFor='hazmatClassId'
                          required
                          name='hazmatClassId'
                          className='mb-1 inline-block'
                        >
                          Hazmat Class
                        </Label>
                        <Controller
                          name='hazmatClassId'
                          control={control}
                          rules={{
                            required: showHazmat
                              ? 'Hazmat class is required'
                              : false,
                          }}
                          render={({ field }) => (
                            <Select
                              onValueChange={(value) =>
                                field.onChange(Number(value))
                              }
                              value={
                                field.value !== undefined &&
                                field.value !== null
                                  ? String(field.value)
                                  : ''
                              }
                            >
                              <SelectTrigger className='w-full mt-1'>
                                <SelectValue placeholder='Select hazmat class' />
                              </SelectTrigger>
                              <SelectContent>
                                {hazmatClassOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={String(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.hazmatClassId && (
                          <Typography
                            variant='body-sm'
                            className='text-error-500'
                          >
                            {errors.hazmatClassId?.message}
                          </Typography>
                        )}
                      </div>

                      {/* Hazmat Prefix */}
                      <div className='mt-2'>
                        <Label
                          htmlFor='hazmatPrefixId'
                          required
                          name='hazmatPrefixId'
                          className='mb-1 inline-block'
                        >
                          Prefix
                        </Label>
                        <Controller
                          name='hazmatPrefixId'
                          control={control}
                          rules={{
                            required: showHazmat ? 'Prefix is required' : false,
                          }}
                          render={({ field }) => (
                            <Select
                              onValueChange={(value) =>
                                field.onChange(Number(value))
                              }
                              value={
                                field.value !== undefined &&
                                field.value !== null
                                  ? String(field.value)
                                  : ''
                              }
                            >
                              <SelectTrigger className='w-full mt-1'>
                                <SelectValue placeholder='Select prefix' />
                              </SelectTrigger>
                              <SelectContent>
                                {hazmatPrefixOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={String(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.hazmatPrefixId && (
                          <Typography
                            variant='body-sm'
                            className='text-error-500'
                          >
                            {errors.hazmatPrefixId?.message}
                          </Typography>
                        )}
                      </div>

                      {/* Hazmat Code */}
                      <div className='mt-2'>
                        <Label
                          htmlFor='hazmatCode'
                          required
                          name='hazmatCode'
                          className='mb-1 inline-block'
                        >
                          Hazmat Code
                        </Label>
                        <Input
                          id='hazmatCode'
                          {...register('hazmatCode', {
                            required: showHazmat
                              ? 'Hazmat code is required'
                              : false,
                            pattern: {
                              value: /^\d{4}$/,
                              message: 'Hazmat code must be 4 digits',
                            },
                          })}
                          placeholder='Enter 4-digit code'
                          maxLength={4}
                          className={
                            errors.hazmatCode ? 'border-error-500' : ''
                          }
                        />
                        {errors.hazmatCode && (
                          <Typography
                            variant='body-sm'
                            className='text-error-500'
                          >
                            {errors.hazmatCode?.message}
                          </Typography>
                        )}
                      </div>

                      {/* Chemical Name */}
                      <div className='mt-2'>
                        <Label
                          htmlFor='hazmatChemicalName'
                          required
                          name='hazmatChemicalName'
                          className='mb-1 inline-block'
                        >
                          Chemical Name
                        </Label>
                        <Input
                          id='hazmatChemicalName'
                          {...register('hazmatChemicalName', {
                            required: showHazmat
                              ? 'Chemical name is required'
                              : false,
                          })}
                          placeholder='Enter chemical name'
                          className={
                            errors.hazmatChemicalName ? 'border-error-500' : ''
                          }
                        />
                        {errors.hazmatChemicalName && (
                          <Typography
                            variant='body-sm'
                            className='text-error-500'
                          >
                            {errors.hazmatChemicalName?.message}
                          </Typography>
                        )}
                      </div>

                      {/* Emergency Contact Number */}
                      <div className='mt-2'>
                        <Label
                          htmlFor='hazmatEmergencyContactNumber'
                          required
                          name='hazmatEmergencyContactNumber'
                          className='mb-1 inline-block'
                        >
                          Emergency Contact Number
                        </Label>
                        <Controller
                          name='hazmatEmergencyContactNumber'
                          control={control}
                          rules={{
                            required: showHazmat
                              ? 'Emergency contact number is required'
                              : false,
                            validate: (value) => {
                              if (!value && !showHazmat) return true;
                              if (!value && showHazmat)
                                return 'Emergency contact number is required';
                              if (!value) return true;
                              // Strip all non-digits and check length
                              const phoneDigits = value.replace(/[^\d]/g, '');
                              if (phoneDigits.length < 10) {
                                return 'Phone number must be at least 10 digits';
                              }
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              id='hazmatEmergencyContactNumber'
                              {...field}
                              onChange={(e) => {
                                const formatted = FormatPhoneNumber(
                                  e.target.value
                                );
                                field.onChange(formatted);
                              }}
                              placeholder='(555) 555-5555'
                              type='tel'
                              className={
                                errors.hazmatEmergencyContactNumber
                                  ? 'border-error-500'
                                  : ''
                              }
                            />
                          )}
                        />
                        {errors.hazmatEmergencyContactNumber && (
                          <Typography
                            variant='body-sm'
                            className='text-error-500'
                          >
                            {errors.hazmatEmergencyContactNumber?.message}
                          </Typography>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <Flex
                    gap='sm'
                    className='border-t border-neutral-300 bg-neutral-100 px-4 py-3 rounded-b-lg'
                  >
                    <Button
                      type='button'
                      variant='outline'
                      onClick={handleClose}
                      disabled={isLoading}
                      buttonNamePosthog={
                        ButtonNamePosthog.CancelLocationCreation
                      }
                      className='flex-1 text-sm'
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      buttonNamePosthog={ButtonNamePosthog.CreateCommodity}
                      disabled={isLoading}
                      className='flex-1 gap-2 [&>svg]:stroke-white! text-sm'
                    >
                      {isLoading ? (
                        <>Creating...</>
                      ) : (
                        <>
                          <Save className='h-4 w-4' />
                          Create
                        </>
                      )}
                    </Button>
                  </Flex>
                </div>
              </form>
            </FormProvider>
          </ExtendedFormProvider>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
